import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Badge,
    Table,
    Text,
    Loader,
    CopyButton,
    ActionIcon,
    Tooltip,
} from '@mantine/core';
import {
    IconGlobe,
    IconCopy,
    IconCheck,
    IconChartPie, IconHome,
} from '@tabler/icons-react';
import styled from 'styled-components';
import { getDashboardData } from '../../../store/reducers/propertyManager';
import Spinner from "../../../components/Spinner/v1/Spinner";

const STATUS_COLORS = {
    Active: 'green',
    Paused: 'yellow',
    Inactive: 'gray',
};

const PropertyManagerDashboard = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);

    const totalActiveUnits = useSelector((s) => s.propertyManager.totalActiveUnits) || 0;
    const totalPortfolioUnits = useSelector((s) => s.propertyManager.totalPortfolioUnits) || 0;
    const penetrationRate = useSelector((s) => s.propertyManager.penetrationRate) || 0;
    const propertyList = useSelector((s) => s.propertyManager.propertyInfo) || [];

    useEffect(() => {
        setIsLoading(true);
        dispatch(getDashboardData({ setLoading: setIsLoading }));
    }, [dispatch]);

    function monthsPassed(dateString) {
        const past = new Date(dateString);
        const now = new Date();

        const years = now.getFullYear() - past.getFullYear();
        const months = now.getMonth() - past.getMonth();
        let total = years * 12 + months;

        // Subtract 1 if we haven't reached the same day-of-month yet
        if (now.getDate() < past.getDate()) {
            total -= 1;
        }

        return total;
    }

    const rows = propertyList.map((unit) => (
        <Table.Tr key={unit.propertyId}>
            <Table.Td>{unit.propertyName}</Table.Td>
            <Table.Td>{unit.totalSubscriberCount}</Table.Td>
            <Table.Td>{unit.totalUnitCount}</Table.Td>
            <Table.Td>{unit.onboardingDate}</Table.Td>
            <Table.Td>{monthsPassed(unit.onboardingDate)}</Table.Td>
        </Table.Tr>
    ));

    return (
        <PageWrapper>
            <PageHeader>
                <Text fw={800} fz={28} tt="uppercase" c="#1a1a2e">
                    Consolidated Dashboard
                </Text>
            </PageHeader>

            {isLoading && <Spinner />}

            <StatsGrid>
                <StatCard>
                    <StatCardHeader>
                        <StatLabel>Total Active Units (Global)</StatLabel>
                        <IconGlobe size={18} color="#adb5bd" stroke={1.5} />
                    </StatCardHeader>
                    <StatNumber>{totalActiveUnits.toLocaleString()}</StatNumber>
                    <StatSub>Active units</StatSub>
                </StatCard>

                <StatCard>
                    <StatCardHeader>
                        <StatLabel>Total Portfolio Units</StatLabel>
                        <IconHome size={18} color="#adb5bd" stroke={1.5} />
                    </StatCardHeader>
                    <StatNumber>{totalPortfolioUnits.toLocaleString()}</StatNumber>
                </StatCard>

                <StatCard>
                    <StatCardHeader>
                        <StatLabel>Penetration Rate</StatLabel>
                        <IconChartPie size={18} color="#adb5bd" stroke={1.5} />
                    </StatCardHeader>
                    <StatNumber>{penetrationRate}%</StatNumber>
                </StatCard>

                {/*<ChartPlaceholder>*/}
                {/*    <ChartPlaceholderInner />*/}
                {/*</ChartPlaceholder>*/}
            </StatsGrid>

            <SectionTitle>Global Portfolio Units</SectionTitle>

            <TableWrapper>
                <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Property Name</Table.Th>
                            <Table.Th>Active Units</Table.Th>
                            <Table.Th>Total Units</Table.Th>
                            <Table.Th>Active Since</Table.Th>
                            <Table.Th>Duration Active (Months)</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rows.length ? rows : (
                            <Table.Tr>
                                <Table.Td colSpan={5}>
                                    <Text c="dimmed" ta="center" fz="sm" py="md">No units found.</Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                    </Table.Tbody>
                </Table>
            </TableWrapper>
        </PageWrapper>
    );
};

export default PropertyManagerDashboard;

const PageWrapper = styled.div`
    padding: 28px 32px;
    height: 100%;
    overflow-y: auto;
`;

const PageHeader = styled.div`
    margin-bottom: 24px;
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 16px;
    margin-bottom: 32px;

    @media (max-width: 1100px) {
        grid-template-columns: 1fr 1fr;
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const StatCard = styled.div`
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
`;

const StatCardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
`;

const StatLabel = styled.p`
    font-size: 12px;
    font-weight: 600;
    color: #868e96;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin: 0;
`;

const StatNumber = styled.p`
    font-size: 36px;
    font-weight: 800;
    color: #1a1a2e;
    margin: 4px 0;
    line-height: 1;
`;

const StatSub = styled.p`
    font-size: 12px;
    color: #868e96;
    margin: 0;
`;

const ChartPlaceholder = styled.div`
    background: #1a1a2e;
    border-radius: 8px;
    overflow: hidden;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ChartPlaceholderInner = styled.div`
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
`;

const SectionTitle = styled.h2`
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #1a1a2e;
    margin: 0 0 12px 0;
`;

const TableWrapper = styled.div`
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;
`;
